import React, { useMemo, useState } from 'react';

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  BookMarkedIcon,
  BookOpenTextIcon,
  FileTextIcon,
  GlobeIcon,
  InfoIcon,
  MessagesSquareIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { MAX_FETCHED_DOCUMENTS } from '@/api/endpoints';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { Button } from '@/components/ui/button';
import { SimpleFilterCombobox } from '@/components/ui/custom/SimpleFilterCombobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useColumnSizing } from '@/components/ui/table/useColumnSizing';
import { cn } from '@/lib/utils';
import { DATASOURCE_TYPES_BY_TAB } from '@/modules/collections/constants';
import { DatasourceDocument, DatasourceTab } from '@/modules/collections/types';
import { TABLE_PAGINATION } from '@/util/constants';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  rowSelection: Record<string, boolean>;
  setRowSelection: (rowSelection: Record<string, boolean>) => void;
  columnFilters: ColumnFiltersState;
  setColumnFilters: (columnFilters: ColumnFiltersState) => void;
}

/**
 * DataTable component for displaying publish documents with filtering, sorting, and pagination.
 *
 * @template TData - Extends `DatasourceDocument` representing a document row.
 * @template TValue - Cell value type for the table.
 * @param {Object} props - Component props
 * @param {ColumnDef<TData, TValue>[]} props.columns - Column definitions for the table
 * @param {TData[]} props.data - Array of data rows to render
 * @param {Record<string, boolean>} props.rowSelection - Current row selection state
 * @param {(rowSelection: Record<string, boolean>) => void} props.setRowSelection - Setter for row selection state
 * @param {ColumnFiltersState} props.columnFilters - Current column filters
 * @param {(columnFilters: ColumnFiltersState) => void} props.setColumnFilters - Setter for column filters
 * @returns {JSX.Element} Rendered table component
 */
export function DataTable<TData extends DatasourceDocument, TValue>({
  columns,
  data,
  rowSelection,
  setRowSelection,
  columnFilters,
  setColumnFilters,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // The compiler skips this component; only plain values reach its children.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.document_id,
    initialState: { pagination: { pageSize: TABLE_PAGINATION } },
    state: { sorting, columnFilters, rowSelection, globalFilter },
  });

  const totalPages = table.getPageCount();
  const shouldShowPagination = totalPages > 1;

  const { tableMinWidth, getWidth, getMinWidth, getMaxWidth } = useColumnSizing(columns);

  const typeFilterOptions = useMemo(
    () => [
      { label: t('collections.tabs.all_types'), value: 'all' },
      { label: t('collections.tabs.faq'), value: 'faq' },
      { label: t('collections.tabs.documents'), value: 'documents' },
      { label: t('collections.tabs.webpages'), value: 'webpages' },
      { label: t('collections.tabs.external'), value: 'external' },
    ],
    [t]
  );

  const currentTypeFilter = useMemo(() => {
    const typeFilter = table.getColumn('type')?.getFilterValue() as string[];
    if (!typeFilter || typeFilter.length === 0) {
      return 'all';
    }

    for (const [tab, types] of Object.entries(DATASOURCE_TYPES_BY_TAB)) {
      if (types.every((type) => typeFilter.includes(type))) {
        return tab as DatasourceTab;
      }
    }
    return 'all';
  }, [table, columnFilters]);

  const typeFilterIcons: Record<string, React.ReactNode> = {
    all: <BookMarkedIcon />,
    faq: <MessagesSquareIcon />,
    documents: <FileTextIcon />,
    webpages: <GlobeIcon />,
    external: <BookOpenTextIcon />,
  };

  const handleTypeFilterChange = (value: string) => {
    if (value === 'all') {
      table.getColumn('type')?.setFilterValue('');
    } else {
      table.getColumn('type')?.setFilterValue(DATASOURCE_TYPES_BY_TAB[value as DatasourceTab]);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Search and Filter Controls */}
      <div className="mb-4 flex shrink-0 items-center justify-between pl-1">
        <SearchBar
          placeholder={t('collections.search_drafts')}
          value={globalFilter ?? ''}
          onChange={(event) => setGlobalFilter(event.target.value)}
        />

        <SimpleFilterCombobox
          options={typeFilterOptions}
          value={currentTypeFilter}
          onChange={handleTypeFilterChange}
          icon={typeFilterIcons[currentTypeFilter]}
        />
      </div>

      {/* Table */}
      <Table
        className="h-full w-full"
        containerClassName="min-h-0 flex-1 [&>div:last-child]:from-background"
        fixedLayout={tableMinWidth !== undefined}
        minWidth={tableMinWidth}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-12">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: getWidth(header.column),
                      minWidth: getMinWidth(header.column),
                      maxWidth: getMaxWidth(header.column),
                    }}
                    className={cn(
                      'sticky top-0 z-10 bg-card',
                      header.column.columnDef.meta?.isNumeric ? 'text-right' : '',
                      header.column.columnDef.meta?.className
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="h-12 cursor-pointer"
                  onClick={() => {
                    setRowSelection({ ...rowSelection, [row.id]: !rowSelection[row.id] });
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        width: getWidth(cell.column),
                        minWidth: getMinWidth(cell.column),
                        maxWidth: getMaxWidth(cell.column),
                      }}
                      className={cn(
                        cell.column.columnDef.meta?.isNumeric
                          ? 'mono text-right text-sm leading-5'
                          : '',
                        cell.column.columnDef.meta?.className
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={table.getAllColumns()?.length} className="h-24 text-center">
                {t('analytics.no_data')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {table.getFilteredRowModel().rows.length > TABLE_PAGINATION && (
        <div className="mt-4 flex h-10 shrink-0 items-center justify-between space-x-2">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {t('table.resource.files_other', { count: table.getFilteredRowModel().rows.length })}
              {data.length >= MAX_FETCHED_DOCUMENTS && (
                <InfoTooltip
                  content={t('collections.publish_modal.limit_reached', {
                    0: MAX_FETCHED_DOCUMENTS,
                  })}
                >
                  <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                </InfoTooltip>
              )}
            </span>
            {totalPages > 0 && (
              <>
                <span className="text-xs leading-none text-muted-foreground">·</span>
                <span>
                  {t('table.page', { 0: table.getState().pagination.pageIndex + 1, 1: totalPages })}
                </span>
              </>
            )}
          </div>
          {shouldShowPagination && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline-neutral"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t('table.previous')}
              </Button>
              <Button
                variant="outline-neutral"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {t('table.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
